import { getDb } from './db';
import { opfsAdapter } from './opfs';
import type { BlobKind } from './types';

/**
 * Unified asset blob storage: originals (and previews/thumbnails) are
 * written to OPFS when supported, and transparently fall back to an
 * IndexedDB-backed table otherwise. Callers never need to know which
 * backend served a given asset.
 */
function blobKey(projectId: string, assetId: string, kind: BlobKind): string {
  return `${projectId}:${assetId}:${kind}`;
}

export const assetBlobStore = {
  async put(projectId: string, assetId: string, kind: BlobKind, blob: Blob): Promise<void> {
    if (opfsAdapter.isSupported()) {
      await opfsAdapter.write(projectId, assetId, kind, blob);
      return;
    }
    const db = getDb();
    await db.assetBlobs.put({
      key: blobKey(projectId, assetId, kind),
      projectId,
      assetId,
      kind,
      mimeType: blob.type || 'application/octet-stream',
      blob,
      byteSize: blob.size,
    });
  },

  async get(projectId: string, assetId: string, kind: BlobKind): Promise<Blob | null> {
    if (opfsAdapter.isSupported()) {
      const blob = await opfsAdapter.read(projectId, assetId, kind);
      if (blob) return blob;
      // Fall through to IndexedDB in case this asset was written before OPFS
      // became available (e.g. browser upgrade mid-session).
    }
    const db = getDb();
    const record = await db.assetBlobs.get(blobKey(projectId, assetId, kind));
    return record?.blob ?? null;
  },

  async remove(projectId: string, assetId: string, kind: BlobKind): Promise<void> {
    if (opfsAdapter.isSupported()) {
      await opfsAdapter.remove(projectId, assetId, kind);
    }
    const db = getDb();
    await db.assetBlobs.delete(blobKey(projectId, assetId, kind));
  },

  async removeAllForAsset(projectId: string, assetId: string): Promise<void> {
    await Promise.all(
      (['original', 'preview', 'thumbnail'] as BlobKind[]).map((kind) =>
        this.remove(projectId, assetId, kind),
      ),
    );
  },

  async removeAllForProject(projectId: string): Promise<void> {
    if (opfsAdapter.isSupported()) {
      await opfsAdapter.removeAllForProject(projectId);
    }
    const db = getDb();
    await db.assetBlobs.where('projectId').equals(projectId).delete();
  },

  async estimateProjectBytes(projectId: string): Promise<number> {
    const db = getDb();
    const records = await db.assetBlobs.where('projectId').equals(projectId).toArray();
    return records.reduce((sum, r) => sum + r.byteSize, 0);
  },
};
